/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Ring2All Team
 📄 File: MetadataModal.tsx
 🗓️ Created: 2025-12-02
 🧩 Description:
    Modal para mostrar metadata de audit logs en formato legible
────────────────────────────────────────────────────────────────*/

import { X, Copy, Check } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { useState } from 'react';

interface MetadataModalProps {
  open: boolean;
  metadata: Record<string, any>;
  action: string;
  resource: string | null;
  timestamp: string;
  onClose: () => void;
}

export function MetadataModal({
  open,
  metadata,
  action,
  resource,
  timestamp,
  onClose,
}: MetadataModalProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    const jsonString = JSON.stringify(metadata, null, 2);
    navigator.clipboard.writeText(jsonString);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-3xl max-h-[80vh] overflow-hidden rounded-lg border border-border bg-card shadow-lg">
        {/* Header */}
        <div className="flex items-start justify-between border-b border-border/60 p-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Audit Log Metadata</h3>
            <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
              <span className="font-medium">{action}</span>
              {resource && (
                <>
                  <span>•</span>
                  <span>{resource}</span>
                </>
              )}
              <span>•</span>
              <span>{new Date(timestamp).toLocaleString()}</span>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="max-h-[60vh] overflow-auto bg-muted/40 p-4">
          <pre className="whitespace-pre-wrap font-mono text-sm text-foreground">
            {JSON.stringify(metadata, null, 2)}
          </pre>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 border-t border-border/60 bg-muted/30 p-4">
          <Button variant="outline" size="sm" onClick={handleCopy}>
            {copied ? (
              <>
                <Check className="mr-2 h-4 w-4" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="mr-2 h-4 w-4" />
                Copy JSON
              </>
            )}
          </Button>
          <Button size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

